import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Input, ComboBox, useDialog } from '@gokrew/ui';
import { addCrmActivity, getCrmDeals, updateCrmActivity } from '@/api/crm';
import type { CrmActivity, CrmActivityInput } from '@/api/crm';
import {
  activityDates,
  useCrmFormat,
  nowLocal,
  toLocalDate,
  toLocalInput,
} from './crm-format';
import { CrmButton, CrmRequired } from './CrmShared';
import styles from './Crm.module.css';
/** The date field's starting value: a task's day, else when it happened. */
const startingDate = (activity?: CrmActivity) => {
  if (!activity) return nowLocal();
  if (activity.kind === 'task')
    return activity.dueAt ? toLocalDate(activity.dueAt) : '';
  const planned = activity.dueAt && !activity.completedAt;
  return toLocalInput(planned ? activity.dueAt! : activity.occurredAt);
};
export const CrmActivityEditor = ({
  dialogId,
  activity,
}: {
  dialogId?: string;
  /** Edit this note, call, meeting or task instead of adding one. */
  activity?: CrmActivity;
}) => {
  const { t } = useCrmFormat();
  const client = useQueryClient();
  const { closeDialog } = useDialog();
  const [search, setSearch] = useState('');
  const [dealId, setDealId] = useState(activity?.dealId ?? '');
  const [kind, setKind] = useState<CrmActivityInput['kind']>(
    (activity?.kind as CrmActivityInput['kind']) ?? 'note'
  );
  const [title, setTitle] = useState(activity?.title ?? '');
  const [body, setBody] = useState(activity?.body ?? '');
  const [due, setDue] = useState(() => startingDate(activity));
  // A task is due on a day (empty until chosen); the others happened at a time.
  const chooseKind = (next: CrmActivityInput['kind']) => {
    setKind(next);
    setDue(next === 'task' ? '' : nowLocal());
  };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const deals = useQuery({
    queryKey: ['crm', 'deals', 'select', search],
    queryFn: () => getCrmDeals({ search, pageSize: 50 }),
  });
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !dealId || (kind === 'task' && !due)) return;
    setBusy(true);
    setError('');
    try {
      if (activity)
        await updateCrmActivity(dealId, activity.id, {
          title: title.trim(),
          body,
          ...activityDates(kind, due),
        });
      else
        await addCrmActivity(dealId, {
          kind,
          title: title.trim(),
          body,
          ...activityDates(kind, due),
        });
      await client.invalidateQueries({ queryKey: ['crm'] });
      if (dialogId) closeDialog(dialogId);
    } catch {
      setError(t('crm.save_error'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className={styles.form} onSubmit={submit}>
      <fieldset disabled={busy} className={styles.grid}>
        {!activity && (
          <label className={styles.fullWidth}>
            <span>
              {t('crm.workspace.opportunity')}
              <CrmRequired />
            </span>
            <ComboBox
              size="sm"
              allowUnselect={false}
              value={dealId}
              placeholder={t('crm.workspace.choose_deal')}
              onSearch={setSearch}
              options={(deals.data?.items ?? []).map((deal) => ({
                value: deal.id,
                label: deal.name,
                description: deal.company?.name,
              }))}
              onChange={(value) => setDealId(value || '')}
            />
          </label>
        )}
        <label>
          {t('crm.workspace.activity_type')}
          <ComboBox
            size="sm"
            allowUnselect={false}
            disabled={!!activity}
            value={kind}
            options={(['note', 'call', 'meeting', 'task'] as const).map(
              (value) => ({
                value,
                label: t(`crm.workspace.activityFilters.${value}`),
              })
            )}
            onChange={(value) => value && chooseKind(value)}
          />
        </label>
        <label>
          <span>
            {t(
              kind === 'task'
                ? 'crm.workspace.due_at'
                : kind === 'note'
                  ? 'crm.workspace.activity_date_note'
                  : 'crm.workspace.activity_date_event'
            )}
            {kind === 'task' && <CrmRequired />}
          </span>
          <Input
            type={kind === 'task' ? 'date' : 'datetime-local'}
            required={kind === 'task'}
            value={due}
            onChange={(event) => setDue(event.target.value)}
          />
        </label>
        <label className={styles.fullWidth}>
          <span>
            {t('crm.workspace.activity_title')}
            <CrmRequired />
          </span>
          <Input
            required
            maxLength={300}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t(`crm.workspace.compose.${kind}`)}
          />
        </label>
      </fieldset>
      <label>
        {t('crm.workspace.activity_details')}
        <textarea
          rows={3}
          maxLength={10000}
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
      </label>
      {(deals.isError || error) && (
        <p className={styles.error} role="alert">
          {error || t('crm.load_error')}
        </p>
      )}
      <div className={styles.actions}>
        <CrmButton
          type="button"
          variant="outline"
          disabled={busy}
          onClick={() => dialogId && closeDialog(dialogId)}
        >
          {t('crm.cancel')}
        </CrmButton>
        <CrmButton
          type="submit"
          variant="adminPrimary"
          disabled={
            busy || !dealId || !title.trim() || (kind === 'task' && !due)
          }
        >
          {t(
            busy
              ? 'crm.saving'
              : activity
                ? 'crm.save'
                : 'crm.workspace.add_activity'
          )}
        </CrmButton>
      </div>
    </form>
  );
};
